import type { HitBox, AttackType } from '@/shared/types';
import type { Fighter } from '@/game/fighters/fighter';

/** Returned by resolveAttack when a hit connects, so callers can spawn effects. */
export interface HitResult {
  /** X coordinate where the attack connected (overlap midpoint). */
  x: number;
  /** Y coordinate where the attack connected (overlap midpoint). */
  y: number;
  /** Which attack type landed. */
  attackType: AttackType;
}

export function checkHitBoxOverlap(a: HitBox, b: HitBox): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

/**
 * Check if the attacker's current attack hits the defender.
 * Returns a HitResult with the contact point and attack type on hit, or null on miss.
 */
export function resolveAttack(attacker: Fighter, defender: Fighter): HitResult | null {
  const attackBox = attacker.getAttackHitBox();
  if (!attackBox) return null;

  const defenderBox = defender.getHitBox();
  if (!checkHitBoxOverlap(attackBox, defenderBox)) return null;

  if (attacker.currentAttack) {
    const damage = attacker.stats.attacks[attacker.currentAttack].damage;
    defender.takeDamage(damage);

    // Compute the midpoint of the overlap rectangle
    const overlapX1 = Math.max(attackBox.x, defenderBox.x);
    const overlapY1 = Math.max(attackBox.y, defenderBox.y);
    const overlapX2 = Math.min(attackBox.x + attackBox.width, defenderBox.x + defenderBox.width);
    const overlapY2 = Math.min(attackBox.y + attackBox.height, defenderBox.y + defenderBox.height);

    return {
      x: (overlapX1 + overlapX2) / 2,
      y: (overlapY1 + overlapY2) / 2,
      attackType: attacker.currentAttack,
    };
  }

  return null;
}

export function updateFacing(fighter1: Fighter, fighter2: Fighter): void {
  // Don't flip facing while either fighter is in hit stun, attacking, or KO'd.
  // With 1.5x scaled hitboxes, knockback can push fighters past each other's center
  // which would cause erratic facing flips mid-animation.
  const locked1 =
    fighter1.state === 'hit' || fighter1.state === 'attack' || fighter1.state === 'ko';
  const locked2 =
    fighter2.state === 'hit' || fighter2.state === 'attack' || fighter2.state === 'ko';
  if (locked1 || locked2) return;

  if (fighter1.x < fighter2.x) {
    fighter1.facing = 'right';
    fighter2.facing = 'left';
  } else {
    fighter1.facing = 'left';
    fighter2.facing = 'right';
  }
}
